/**
 * Zerbay header controller (hz/12). Two custom elements, built on Horizon's Component and DialogComponent:
 *
 *   <zerbay-header>  the Horizon header contract (HDR-45), the Shoes panel (HDR-09), the solid / transparent state (HDR-05),
 *                    the phone-menu openers and the cart badge mirror.
 *   <zerbay-menu>    the phone menu dialog (HDR-12, HDR-24): DialogComponent plus instant close, a no-showModal fallback,
 *                    the cart hand-off and the layer events.
 *
 * Layers (country contract v1, section 5): before a layer opens, the header sends `zb:layer-open` on `document` with
 * detail.layer = 'menu' | 'shoes-panel' | 'search' (the country sheet sends 'country-sheet'). Every other
 * layer closes at once without focus return. The sheet's `zb:layer-close` re-evaluates the solid state.
 *
 * Phone-menu openers: any `[aria-controls="ZbMenu"]` inside <zerbay-header> (the bar's menu button, hz/13's phone search
 * icon). The header listens for their clicks and calls `menu.showDialog(opener)`; a declarative on:click was not used so that
 * the opener can be passed. A tap before this module has run is caught by the inline script of the section, which replays it
 * through customElements.whenDefined('zerbay-menu').
 */
import { Component } from '@theme/component';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { morph } from '@theme/morph';
import { sectionRenderer } from '@theme/section-renderer';
import { lockScroll, prefersReducedMotion, unlockScroll } from '@theme/utilities';
import {
  getIntersectionRoot,
  getScrollEventTarget,
  getScrollTop,
  scrollContainerMediaQuery,
  scrollTo,
} from '@theme/scroll-container';

const DESKTOP = window.matchMedia('(min-width: 960px)');
const OPENERS = '[aria-controls="ZbMenu"]';

/** Open layers that make the header solid: 'menu', 'shoes-panel', 'country-sheet', 'search'. */
const layers = new Set();

/** @param {Element | null | undefined} el */
const isVisible = (el) => Boolean(el && el.getClientRects().length);

/** @param {HTMLElement | null | undefined} el */
const focusEl = (el) => el?.focus({ preventScroll: true });

/** @param {string} layer */
const announce = (layer) => document.dispatchEvent(new CustomEvent('zb:layer-open', { detail: { layer } }));

/* ---------- hz/13: Horizon search modal neutraliser (HDR-16, HDR-43, HZD-29) ----------
   layout/theme.liquid prints Horizon's #search-modal on every page. Its predictive-search-component (defined later by
   predictive-search.js, which that snippet prints after this module in document order) listens for Cmd+K and requests an
   empty state on idle. Outside the theme editor the unused modal is removed before it upgrades; in the editor
   (theme-editor.js selects .search-modal) it stays and only Cmd+K is stopped, without preventDefault. */
if (!window.Shopify?.designMode) {
  document.getElementById('search-modal')?.remove();
} else {
  window.addEventListener(
    'keydown',
    (event) => {
      if (event.metaKey && event.key.toLowerCase() === 'k') event.stopPropagation();
    },
    { capture: true }
  );
}

/**
 * Another modal dialog (cart drawer, country sheet) may still be open: then the scroll-lock class must stay.
 * @param {Element} [except] - The dialog that is closing.
 */
function releaseScrollLock(except) {
  let others;
  try {
    others = document.querySelectorAll('dialog:modal');
  } catch (_) {
    others = document.querySelectorAll('dialog[open]');
  }
  if (![...others].some((dialog) => dialog !== except)) document.documentElement.classList.remove('zb-scroll-lock');
}

/** @param {boolean} expanded */
function setExpanded(expanded) {
  for (const opener of document.querySelectorAll(OPENERS)) opener.setAttribute('aria-expanded', String(expanded));
}

/** Solid: not the home template, scrolled, or any layer open. Transparent only over the home hero at the top. */
function updateSolid() {
  const header = document.getElementById('header-component');
  if (!header) return;

  const sheetOpen = layers.has('country-sheet') || /** @type {any} */ (document.getElementById('ZbCountrySheet'))?.open === true;
  const solid =
    !header.hasAttribute('data-zb-home') ||
    getScrollTop() > 0 ||
    layers.has('menu') ||
    layers.has('shoes-panel') ||
    layers.has('search') ||
    sheetOpen;

  header.classList.toggle('is-solid', solid);
}

/**
 * The phone menu (HDR-12). Refs: `dialog` (required by DialogComponent).
 */
class ZerbayMenu extends DialogComponent {
  /** Horizon's animated close (a field, not a method, so it has to be captured before it is replaced below). */
  #baseClose = this.closeDialog;

  /** Everything DialogComponent calls (Escape, outside click, resize) now goes through close(). */
  closeDialog = () => this.close();

  /** @type {AbortController | null} */
  #abort = null;
  /** @type {Element | null} */
  #opener = null;
  #scrollY = 0;
  #opening = false;
  #abortOpen = false;
  #active = false;
  #fallback = false;
  #noFocus = false;
  /** @type {Promise<void> | null} */
  #closing = null;
  #epoch = 0;
  /** hz/13: phone search glue. @type {MenuSearch | null} */
  #search = null;

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this.refs;
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const closeAtOnce = () => this.close({ instant: true, noFocus: true });
    this.#search ??= new MenuSearch(this); // hz/13

    this.addEventListener('dialog:open', this.#onOpened, { signal });
    this.addEventListener('dialog:close', this.#onClosed, { signal });
    dialog.addEventListener('close', this.#onNativeClose, { signal });
    dialog.addEventListener('click', this.#onClick, { signal });
    // Escape on a modal dialog: DialogComponent handles the key; the native cancel is routed to the same close.
    dialog.addEventListener(
      'cancel',
      (event) => {
        event.preventDefault();
        this.closeDialog();
      },
      { signal }
    );
    document.addEventListener('click', this.#onOutside, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    // Another layer opened (country sheet, search): leave at once, no focus return.
    document.addEventListener(
      'zb:layer-open',
      (event) => {
        if (/** @type {CustomEvent} */ (event).detail?.layer !== 'menu') closeAtOnce();
      },
      { signal }
    );
    // bfcache restore and crossing 960 px: the menu does not exist at desktop width.
    window.addEventListener('pageshow', (event) => event.persisted && closeAtOnce(), { signal });
    DESKTOP.addEventListener('change', closeAtOnce, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#search?.destroy(); // hz/13
    this.#search = null;
    this.#epoch += 1;
    this.#active = false;
    this.#opening = false;
    layers.delete('menu');
    setExpanded(false);
    releaseScrollLock();
    updateSolid();
  }

  /**
   * Opens the menu. A call during a close waits for that close to finish.
   * @param {Element | Event | null} [opener] - The element that asked (gets focus back on close).
   */
  showDialog(opener = null) {
    const { dialog } = this.refs;
    const element = opener instanceof Element ? opener : null;

    if (this.#closing) {
      // Open once the running close has finished (the stored promise settles after #closing is cleared).
      // A takeover (instant close, layer, unload) in between cancels it.
      const epoch = this.#epoch;
      this.#closing.then(() => this.#epoch === epoch && this.showDialog(element));
      return;
    }
    if (this.#opening || this.#isOpen()) return;

    this.#opener = element;
    this.#opening = true;
    this.#abortOpen = false;
    this.#scrollY = getScrollTop();
    layers.add('menu');
    announce('menu');
    updateSolid();

    if (typeof dialog.showModal !== 'function') {
      this.#openFallback();
      return;
    }

    super.showDialog();
    // DialogComponent opens in a rAF; if showModal threw there, this second frame callback finds the dialog still closed.
    requestAnimationFrame(() => {
      if (this.#opening && !dialog.open) this.#openFallback();
    });
  }

  /**
   * Closes the menu.
   * @param {{ instant?: boolean, noFocus?: boolean }} [options] - instant: no animation; noFocus: no focus return.
   * @returns {Promise<void>}
   */
  close({ instant = false, noFocus = false } = {}) {
    const { dialog } = this.refs;

    this.#epoch += 1; // any close request cancels an open queued during a close

    if (!this.#isOpen()) {
      // Closed before the opening frame ran: the open is cancelled when it happens.
      if (this.#opening) this.#abortOpen = true;
      return this.#closing ?? Promise.resolve();
    }

    if (noFocus) this.#noFocus = true;

    if (instant || prefersReducedMotion() || typeof dialog.close !== 'function') {
      this.#closeNow();
      return Promise.resolve();
    }

    if (!this.#closing) {
      this.#closing = this.#baseClose().finally(() => {
        this.#closing = null;
      });
    }
    return this.#closing;
  }

  #isOpen() {
    const { dialog } = this.refs;
    return Boolean(dialog.open || dialog.hasAttribute('open'));
  }

  /** Closes the native dialog directly (no animation) and runs the same clean-up as an animated close. */
  #closeNow() {
    const { dialog } = this.refs;

    dialog.classList.remove('dialog-closing');
    unlockScroll(dialog);
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    dialog.removeAttribute('open');
    scrollTo({ top: this.#scrollY, behavior: 'instant' });
    this.dispatchEvent(new DialogCloseEvent());
  }

  /** No usable showModal: the open attribute, the header raised above the page, a Tab trap (keydown) and an outside click. */
  #openFallback() {
    const { dialog } = this.refs;

    if (this.#abortOpen) {
      this.#abortOpen = false;
      this.#opening = false;
      unlockScroll(dialog); // Horizon's rAF locked before showModal threw
      layers.delete('menu');
      updateSolid();
      return;
    }

    this.#fallback = true;
    dialog.setAttribute('data-zb-fallback', '');
    dialog.setAttribute('open', '');
    lockScroll(dialog);
    this.closest('#header-component')?.setAttribute('data-zb-open', '');
    this.#finishOpen();
  }

  #finishOpen() {
    const { dialog } = this.refs;

    this.#opening = false;
    this.#active = true;
    document.documentElement.classList.add('zb-scroll-lock');
    setExpanded(true);

    // Every opening starts at the top of the menu.
    const body = dialog.querySelector('.zb-menu__body');
    if (body) body.scrollTop = 0;
    // hz/13: the phone search icon opens the menu with the search field focused
    focusEl(
      this.#opener?.getAttribute('data-zb-focus') === 'search'
        ? dialog.querySelector('.zb-menu__input')
        : dialog.querySelector('.zb-menu__close')
    );
    updateSolid();
  }

  /** DialogComponent's `dialog:open`, sent after showModal. */
  #onOpened = () => {
    if (!this.#opening) return;

    const cancelled = this.#abortOpen;
    this.#abortOpen = false;
    this.#finishOpen();
    if (cancelled) this.close({ instant: true, noFocus: true });
  };

  /** `dialog:close`, sent after an animated or an instant close: the one clean-up for every close path. */
  #onClosed = () => {
    if (!this.#active) return;

    const { dialog } = this.refs;
    const noFocus = this.#noFocus;
    const opener = this.#opener;

    this.#search?.reset(); // hz/13: results reset and the field clears whenever the menu closes
    this.#active = false;
    this.#fallback = false;
    this.#noFocus = false;
    this.#opener = null;
    dialog.removeAttribute('data-zb-fallback');
    dialog.classList.remove('dialog-closing');
    this.closest('#header-component')?.removeAttribute('data-zb-open');
    layers.delete('menu');
    setExpanded(false);
    releaseScrollLock(dialog);
    updateSolid();

    if (noFocus) return;

    // Focus returns to the opener, or to the first visible opener when that one is hidden.
    focusEl(isVisible(opener) ? /** @type {HTMLElement} */ (opener) : [...document.querySelectorAll(OPENERS)].find(isVisible));
  };

  /** The browser closed the dialog itself (Android back, a second Escape): Horizon never sees it, so unlock here. */
  #onNativeClose = () => {
    if (!this.#active || this.#isOpen()) return;
    unlockScroll(this.refs.dialog);
    this.#onClosed();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const { dialog } = this.refs;
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // A click that lands on the dialog element itself is a backdrop click (iOS gives none to the document).
    if (target === dialog || target.closest('[data-zb-close]')) {
      this.closeDialog();
    } else if (target.closest('[data-zb-handoff]')) {
      this.#handOff();
    } else if (target.closest('a[href]')) {
      this.closeDialog();
    }
  };

  /** Fallback only: a click outside the open menu closes it. */
  #onOutside = (event) => {
    const { dialog } = this.refs;
    if (!this.#fallback || !this.#active || !(event.target instanceof Element) || dialog.contains(event.target)) return;
    // The click that opened the fallback menu bubbles here too: an opener is not "outside".
    if (event.target.closest(OPENERS)) return;

    event.preventDefault();
    this.closeDialog();
  };

  /** Fallback only: Escape closes and Tab stays inside the menu (a modal dialog does both itself). */
  #onKeydown = (event) => {
    if (!this.#fallback || !this.#active) return;

    const { dialog } = this.refs;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDialog();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = [
      ...dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])'
      ),
    ].filter(isVisible);
    if (!focusable.length) return;

    const first = /** @type {HTMLElement} */ (focusable[0]);
    const last = /** @type {HTMLElement} */ (focusable[focusable.length - 1]);
    const outside = !dialog.contains(document.activeElement);
    if (event.shiftKey && (document.activeElement === first || outside)) {
      event.preventDefault();
      focusEl(last);
    } else if (!event.shiftKey && (document.activeElement === last || outside)) {
      event.preventDefault();
      focusEl(first);
    }
  };

  /** The menu's cart button: close at once, then click the bar's cart (opens the drawer; on /cart it follows the link). */
  #handOff() {
    const cart = document.querySelector('#header-component .zb-hd__bar .zb-hd__cart');

    if (!(cart instanceof HTMLElement) || !isVisible(cart)) {
      this.closeDialog();
      return;
    }
    this.close({ instant: true, noFocus: true });
    focusEl(cart);
    cart.click();
  }
}

/**
 * @typedef {object} HeaderRefs
 * @property {HTMLElement} [nav]
 * @property {HTMLButtonElement} [shoesButton]
 * @property {HTMLElement} [panel]
 * @property {HTMLElement} [scrim]
 *
 * @extends Component<HeaderRefs>
 */
class ZerbayHeader extends Component {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {AbortController | null} */
  #scrollAbort = null;
  /** @type {IntersectionObserver | null} */
  #sticky = null;
  /** @type {ResizeObserver[]} */
  #resizers = [];
  /** @type {MutationObserver[]} */
  #mutators = [];
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    // Capture: some page scripts stop the propagation of their clicks.
    document.addEventListener('click', this.#onOutside, { capture: true, signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('zb:layer-open', this.#onLayerOpen, { signal });
    document.addEventListener('zb:layer-close', this.#onLayerClose, { signal });
    window.addEventListener('pageshow', this.#onPageShow, { signal });
    window.addEventListener('load', updateSolid, { signal });
    DESKTOP.addEventListener('change', () => this.closePanel(), { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#onContainerChange, { signal });

    this.#keepContract();
    this.#watchSticky();
    this.#watchScroll();
    this.#mirrorCartBadge();
    updateSolid();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#scrollAbort?.abort();
    this.#sticky?.disconnect();
    for (const observer of [...this.#resizers, ...this.#mutators]) observer.disconnect();
    this.#resizers = [];
    this.#mutators = [];
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    layers.delete('shoes-panel');
    updateSolid();
  }

  /* ---------- Shoes panel (HDR-09) ---------- */

  get panelOpen() {
    const { panel } = this.refs;
    return Boolean(panel && !panel.hidden);
  }

  openPanel() {
    const { panel, scrim, shoesButton } = this.refs;
    if (!panel || !shoesButton || this.panelOpen) return;

    layers.add('shoes-panel');
    announce('shoes-panel');
    panel.hidden = false;
    if (scrim) scrim.hidden = false;
    shoesButton.setAttribute('aria-expanded', 'true');
    updateSolid();
  }

  /** @param {{ focus?: boolean }} [options] - focus: put the focus back on the Shoes button (Escape). */
  closePanel({ focus = false } = {}) {
    const { panel, scrim, shoesButton } = this.refs;
    if (!panel || !this.panelOpen) return;

    panel.hidden = true;
    if (scrim) scrim.hidden = true;
    shoesButton?.setAttribute('aria-expanded', 'false');
    layers.delete('shoes-panel');
    updateSolid();
    if (focus) focusEl(shoesButton);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const { shoesButton, scrim } = this.refs;
    const opener = target.closest(OPENERS);

    // hz/13: the desktop search icon opens the inline field
    if (target.closest('button.zb-hd__search-d')) {
      /** @type {ZerbayPredictive | null} */ (this.querySelector('zerbay-predictive[data-zb-layer="search"]'))?.openSearch();
      return;
    }

    if (opener) {
      /** @type {ZerbayMenu | null} */ (this.querySelector('zerbay-menu'))?.showDialog(opener);
    } else if (shoesButton?.contains(target)) {
      if (this.panelOpen) this.closePanel();
      else this.openPanel();
    } else if (scrim?.contains(target)) {
      this.closePanel();
    }
  };

  /** A click anywhere outside the nav closes the panel. */
  #onOutside = (event) => {
    const { nav } = this.refs;
    if (this.panelOpen && !(event.target instanceof Node && nav?.contains(event.target))) this.closePanel();
  };

  #onFocusOut = (event) => {
    const { nav } = this.refs;
    const to = event.relatedTarget;
    if (this.panelOpen && to instanceof Node && nav?.contains(event.target) && !nav.contains(to)) this.closePanel();
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.panelOpen) return;

    event.preventDefault();
    this.closePanel({ focus: true });
  };

  /** @param {Event} event */
  #onLayerOpen = (event) => {
    const layer = /** @type {CustomEvent} */ (event).detail?.layer;
    if (!layer || layer === 'shoes-panel') return;

    // 'menu' registers itself; the others (country sheet, search) are tracked here for the solid state.
    if (layer !== 'menu') layers.add(layer);
    this.closePanel();
    updateSolid();
  };

  /** @param {Event} event */
  #onLayerClose = (event) => {
    const layer = /** @type {CustomEvent} */ (event).detail?.layer;
    if (!layer || layer === 'menu' || layer === 'shoes-panel') return;

    layers.delete(layer);
    updateSolid();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.closePanel();
    updateSolid();
  };

  /* ---------- Horizon header contract (HDR-45) ---------- */

  /** --header-height, --header-group-height and data-sticky-state: what header.js writes, which this header does not load. */
  #keepContract() {
    const header = document.getElementById('header-component');
    const group = document.getElementById('header-group');
    if (!header) return;

    const heightObserver = new ResizeObserver(([entry]) => {
      const height = entry?.borderBoxSize?.[0]?.blockSize ?? header.offsetHeight;
      document.body.style.setProperty('--header-height', `${Math.round(height)}px`);
    });
    heightObserver.observe(header);
    this.#resizers.push(heightObserver);

    if (!group) return;

    // The group height is the sum of all its children, so every callback measures them all.
    const measure = () => {
      let total = 0;
      for (const child of group.children) if (child instanceof HTMLElement) total += child.offsetHeight;
      document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
    };
    const groupObserver = new ResizeObserver(measure);
    const observeChildren = () => {
      for (const child of group.children) groupObserver.observe(child);
    };
    observeChildren();

    const childObserver = new MutationObserver(() => {
      observeChildren();
      measure();
    });
    childObserver.observe(group, { childList: true });

    this.#resizers.push(groupObserver);
    this.#mutators.push(childObserver);
  }

  /** 'active' while the bar is stuck at top 0 (the -1px rootMargin makes a stuck bar read as not fully visible), 'inactive' otherwise; the root changes at 990 px. */
  #watchSticky() {
    const header = document.getElementById('header-component');
    if (!header) return;

    this.#sticky?.disconnect();
    this.#sticky = new IntersectionObserver(
      ([entry]) => {
        if (entry) header.dataset.stickyState = entry.intersectionRatio < 1 ? 'active' : 'inactive';
      },
      { threshold: 1, root: getIntersectionRoot(), rootMargin: '-1px 0px 0px 0px' }
    );
    this.#sticky.observe(header);
  }

  /* ---------- Solid / transparent state (HDR-05) ---------- */

  #watchScroll() {
    this.#scrollAbort?.abort();
    this.#scrollAbort = new AbortController();
    getScrollEventTarget().addEventListener('scroll', this.#onScroll, { passive: true, signal: this.#scrollAbort.signal });
  }

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      updateSolid();
    });
  };

  #onContainerChange = () => {
    this.#watchScroll();
    this.#watchSticky();
    updateSolid();
  };

  /* ---------- Cart badge in the phone menu (HDR-19) ---------- */

  /** One source of truth: the bar bubble that cart-icon.js keeps current (cart events, bfcache, sessionStorage). */
  #mirrorCartBadge() {
    const count = this.querySelector('.zb-hd__bar [ref="cartBubbleCount"]');
    const badge = this.querySelector('.zb-menu__badge');
    if (!count || !badge) return;

    const sync = () => {
      const items = parseInt(count.textContent ?? '', 10);
      badge.textContent = items > 0 ? String(items) : '';
    };
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(count, { childList: true, characterData: true, subtree: true });
    this.#mutators.push(observer);
  }
}


/* =====================================================================================================================
   hz/13: header search. One shared predictive controller (<zerbay-predictive>) serves the desktop inline field
   (HDR-16, SC-20) and the phone-menu field (HDR-14, HDR-15). DesktopSearch is the desktop open / close layer;
   MenuSearch is the phone-menu glue (is-searching state, keyboard inset, Escape, touch drag). Results come from
   sections/zerbay-predictive-search.liquid through the Section Rendering API.
   ===================================================================================================================== */

/**
 * Section CSS (Shopify stylesheet subsetting): a Section Rendering response may carry `style[data-section-stylesheet]`.
 * Exactly one copy is kept for the page in `style[data-zb-ps-style]` (Horizon's injectSectionStylesheet pattern).
 * @param {Element | null} style - The style element of the response.
 * @param {Element} host
 */
function syncPredictiveStyle(style, host) {
  if (!style) return;

  let shared = document.querySelector('style[data-zb-ps-style]');
  if (!shared) {
    shared = document.createElement('style');
    shared.setAttribute('data-zb-ps-style', '');
    (host.closest('#header-component') ?? document.head).append(shared);
  }
  if (shared.textContent !== style.textContent) shared.textContent = style.textContent;
}

/**
 * @typedef {object} PredictiveRefs
 * @property {HTMLInputElement} [input]
 * @property {HTMLElement} [results]
 *
 * @extends Component<PredictiveRefs>
 */
class ZerbayPredictive extends Component {
  requiredRefs = ['input', 'results'];

  /** @type {AbortController | null} */
  #abort = null;
  /** @type {AbortController | null} */
  #request = null;
  #timer = 0;
  #seq = 0;
  #last = '';
  /** Queries whose answer had no results root: the next try skips the section renderer's cache. */
  #failed = new Set();
  /** @type {DesktopSearch | null} */
  #desktop = null;

  get min() {
    return Number(this.dataset.zbMin) || 1;
  }

  get delay() {
    return Number(this.dataset.zbDelay) || 300;
  }

  /** True while the results box is shown. */
  get showing() {
    return Boolean(this.refs.results) && !this.refs.results.hidden;
  }

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const { input } = this.refs;

    input.addEventListener(this.dataset.zbEvent === 'keyup' ? 'keyup' : 'input', this.#onType, { signal });
    if (this.dataset.zbLayer === 'search') {
      // Focusing the desktop field again re-runs the search for its value (atlantic.js:34896).
      input.addEventListener('focus', this.#onFocus, { signal });
      this.#desktop ??= new DesktopSearch(this);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#desktop?.destroy();
    this.#desktop = null;
    this.reset();
  }

  /** Opens the desktop inline search (no-op on the phone field). */
  openSearch() {
    this.#desktop?.open();
  }

  /** @returns {DesktopSearch | null} */
  get desktop() {
    return this.#desktop;
  }

  #onType = () => this.#queue(this.refs.input.value, { delay: this.delay });

  #onFocus = () => this.#queue(this.refs.input.value, { delay: 0, force: true });

  /**
   * One keystroke (or focus): decide whether a request is due.
   * @param {string} value
   * @param {{ delay: number, force?: boolean }} options
   */
  #queue(value, { delay, force = false }) {
    const query = value.trim();

    // The same trimmed query as the last requested one: keep what is shown.
    if (query.length >= this.min && query === this.#last && !force) return;

    // Every other keystroke is a new query: cancel the timer and the request in flight, take a new sequence number.
    clearTimeout(this.#timer);
    this.#timer = 0;
    this.#abortRequest();
    const seq = ++this.#seq;

    if (query.length < this.min) {
      this.reset();
      return;
    }

    this.#last = query;
    if (delay <= 0) {
      this.#fetch(query, seq);
      return;
    }
    this.#timer = window.setTimeout(() => {
      this.#timer = 0;
      if (seq === this.#seq) this.#fetch(query, seq);
    }, delay);
  }

  #abortRequest() {
    this.#request?.abort();
    this.#request = null;
  }

  /**
   * @param {string} query - The trimmed query that is sent.
   * @param {number} seq - Its sequence number.
   */
  async #fetch(query, seq) {
    const controller = new AbortController();
    this.#request = controller;

    // routes.predictive_search_url is locale-aware (/de, /fr ...). Liquid prints it on the element; Horizon's `Theme` is a global
    // lexical binding (const in a classic script), not a window property, so it is only a second source.
    const route =
      this.dataset.zbUrl || (typeof Theme !== 'undefined' && Theme.routes?.predictive_search_url) || '/search/suggest';
    const url = new URL(route, location.origin);
    url.searchParams.set('q', query);

    let html;
    try {
      html = await sectionRenderer.getSectionHTML('zerbay-predictive-search', !this.#failed.has(query), url, controller.signal);
    } catch (_) {
      if (seq === this.#seq) this.reset(); // a failed request: no error text
      return;
    }
    if (this.#request === controller) this.#request = null;
    if (seq !== this.#seq) return;

    this.#show(html, query);
  }

  /**
   * @param {string} html - The section's HTML.
   * @param {string} query - The trimmed query that was sent for this answer.
   */
  #show(html, query) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const root = parsed.querySelector('[data-zb-ps]');

    if (!(root instanceof HTMLElement)) {
      this.#failed.add(query);
      this.reset();
      return;
    }
    // The sequence number and the abort already exclude stale answers, so a terms mismatch here can only be the server
    // normalising predictive_search.terms: the answer is shown.
    syncPredictiveStyle(parsed.querySelector('style[data-section-stylesheet]'), this);

    const { results } = this.refs;
    const current = results.querySelector('[data-zb-ps]');
    if (current) {
      // morph() defaults to childrenOnly: morph the box's children, so the new root's own attributes (the terms) apply too.
      const next = /** @type {HTMLElement} */ (results.cloneNode(false));
      next.append(document.importNode(root, true));
      morph(results, next);
    } else {
      results.append(document.importNode(root, true));
    }

    results.hidden = false;
    this.dispatchEvent(new CustomEvent('zb-predictive:show', { bubbles: true }));
  }

  /**
   * Reset: clear the timer, bump the sequence, abort, empty and hide the box, forget the last query.
   * @param {{ clear?: boolean }} [options] - clear: also empty the field.
   */
  reset({ clear = false } = {}) {
    clearTimeout(this.#timer);
    this.#timer = 0;
    this.#seq++;
    this.#abortRequest();
    this.#last = '';

    const { results, input } = this.refs;
    if (results) {
      results.hidden = true;
      results.replaceChildren();
    }
    if (clear && input) input.value = '';
    this.dispatchEvent(new CustomEvent('zb-predictive:reset', { bubbles: true }));
  }
}

/**
 * The desktop inline search as a layer (HDR-16, SC-20, SC-21). Not a modal: no inert page, no scroll lock, no backdrop.
 * Closes on: the close button, Escape, an outside pointer-down, focus leaving by KEYBOARD (a script that moves the focus,
 * such as the Klaviyo pop-up, does not close it), another layer opening, crossing 960 px, bfcache restore.
 */
class DesktopSearch {
  #host;
  #life = new AbortController();
  /** @type {AbortController | null} */
  #openAbort = null;
  #open = false;
  /** True from a Tab keydown until its keyup: a focus change in that window was made by the keyboard. */
  #tabbing = false;

  /** @param {ZerbayPredictive} host */
  constructor(host) {
    this.#host = host;
    const { signal } = this.#life;

    host.querySelector('form')?.addEventListener(
      'submit',
      (event) => {
        // An empty (trimmed) query is blocked; otherwise the GET form navigates to routes.search_url?q=
        if (!this.#input.value.trim()) event.preventDefault();
      },
      { signal }
    );
    host.querySelector('[data-zb-search-close]')?.addEventListener('click', () => this.close(), { signal });
    document.addEventListener(
      'zb:layer-open',
      (event) => {
        if (/** @type {CustomEvent} */ (event).detail?.layer !== 'search') this.close({ focus: false });
      },
      { signal }
    );
    window.addEventListener('pageshow', (event) => event.persisted && this.close({ focus: false }), { signal });
    DESKTOP.addEventListener('change', () => this.close({ focus: false }), { signal });
  }

  get isOpen() {
    return this.#open;
  }

  get #input() {
    return /** @type {HTMLInputElement} */ (this.#host.refs.input);
  }

  get #header() {
    return this.#host.closest('#header-component');
  }

  get #icon() {
    return /** @type {HTMLElement | null} */ (this.#header?.querySelector('button.zb-hd__search-d') ?? null);
  }

  open() {
    const form = this.#host.querySelector('form');
    if (this.#open || !form || !DESKTOP.matches) return;

    this.#open = true;
    layers.add('search');
    announce('search'); // closes the country sheet and the Shoes panel
    form.hidden = false;
    this.#header?.classList.add('is-search-open');
    this.#icon?.setAttribute('aria-expanded', 'true');
    updateSolid();

    this.#openAbort = new AbortController();
    const { signal } = this.#openAbort;
    // Capture: some page scripts stop the propagation of their pointer events.
    document.addEventListener('pointerdown', this.#onPointerDown, { capture: true, signal });
    document.addEventListener('keydown', this.#onKeydown, { capture: true, signal });
    document.addEventListener('keydown', this.#onEscape, { signal });
    document.addEventListener('keyup', this.#onKeyup, { capture: true, signal });
    this.#host.addEventListener('focusout', this.#onFocusOut, { signal });

    focusEl(this.#input);
  }

  /**
   * @param {{ focus?: boolean, clear?: boolean }} [options] - focus: put the focus back on the icon; clear: empty the field.
   */
  close({ focus = true, clear = false } = {}) {
    if (!this.#open) return;

    this.#open = false;
    this.#tabbing = false;
    this.#openAbort?.abort();
    this.#openAbort = null;

    const form = this.#host.querySelector('form');
    this.#host.reset({ clear });
    if (form) form.hidden = true;
    this.#header?.classList.remove('is-search-open');
    this.#icon?.setAttribute('aria-expanded', 'false');
    // Not a contract event (only the country sheet sends zb:layer-close): the header's own state is updated here.
    layers.delete('search');
    updateSolid();

    const icon = this.#icon;
    if (focus && isVisible(icon)) focusEl(icon);
  }

  destroy() {
    this.close({ focus: false });
    this.#life.abort();
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#tabbing = false;
    const target = event.target instanceof Node ? event.target : null;
    if (!target || this.#host.contains(target) || this.#icon?.contains(target)) return;
    this.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    this.#tabbing = event.key === 'Tab';
  };

  /** Bubble phase: a handler that already used the Escape (a pop-up, a menu) wins. @param {KeyboardEvent} event */
  #onEscape = (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing) return;

    // Only an Escape that belongs to the search (inside it, or on the page body): a pop-up's own Escape (the Klaviyo form
    // keeps the focus under SC-20) is left alone, and the focus does not move.
    const target = event.target;
    const inside = target instanceof Node && this.#host.contains(target);
    if (!inside && target !== document.body) return;

    event.preventDefault();
    this.close({ clear: true });
  };

  /** @param {KeyboardEvent} event */
  #onKeyup = (event) => {
    if (event.key === 'Tab') this.#tabbing = false;
  };

  /** Focus left the form and the results. Only the keyboard closes (SC-20); a scripted focus does not. */
  #onFocusOut = (event) => {
    const to = event.relatedTarget;
    if (this.#tabbing && to instanceof Node && !this.#host.contains(to)) this.close({ focus: false });
  };
}

/**
 * Phone-menu search glue (HDR-14, HDR-15): the `is-searching` state, the ivory backdrop, the keyboard inset, Escape that
 * clears the results only, and the drag that drops the keyboard.
 */
class MenuSearch {
  #menu;
  #abort = new AbortController();
  /** @type {(() => void) | null} */
  #viewport = null;
  /** @type {number | null} */
  #startY = null;

  /** @param {HTMLElement} menu */
  constructor(menu) {
    this.#menu = menu;
    const { signal } = this.#abort;

    menu.addEventListener('zb-predictive:show', this.#onShow, { signal });
    menu.addEventListener('zb-predictive:reset', this.#onReset, { signal });
    // Capture on the host: Escape is dealt with before DialogComponent's own (bubble) key handler sees it.
    menu.addEventListener('keydown', this.#onKeydown, { capture: true, signal });
    menu.addEventListener(
      'touchstart',
      (event) => {
        this.#startY = event.target instanceof Element && event.target.closest('.zb-menu__results') ? (event.touches[0]?.clientY ?? null) : null;
      },
      { passive: true, signal }
    );
    menu.addEventListener(
      'touchmove',
      (event) => {
        const y = event.touches[0]?.clientY;
        if (this.#startY === null || y === undefined) return;
        // A real scroll (more than 8 px) inside the results drops the keyboard; a tap still follows the link.
        if (Math.abs(y - this.#startY) > 8 && document.activeElement === this.#field) /** @type {HTMLElement} */ (this.#field).blur();
      },
      { passive: true, signal }
    );
  }

  get #predictive() {
    return /** @type {ZerbayPredictive | null} */ (this.#menu.querySelector('zerbay-predictive'));
  }

  get #field() {
    return this.#menu.querySelector('.zb-menu__input');
  }

  get #body() {
    return this.#menu.querySelector('.zb-menu__body');
  }

  get #dialog() {
    return this.#menu.querySelector('dialog');
  }

  /** True while results show. */
  get active() {
    return Boolean(this.#predictive?.showing);
  }

  /** Results reset and the field clears. */
  reset() {
    this.#predictive?.reset({ clear: true });
  }

  destroy() {
    this.#abort.abort();
    this.#watchViewport(false);
  }

  #onShow = () => {
    const body = this.#body;
    body?.classList.add('is-searching');
    this.#dialog?.setAttribute('data-zb-searching', '');
    if (body) body.scrollTop = 0;
    this.#watchViewport(true);
  };

  #onReset = () => {
    const body = this.#body;
    body?.classList.remove('is-searching');
    this.#dialog?.removeAttribute('data-zb-searching');
    if (body) body.scrollTop = 0;
    this.#watchViewport(false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || event.target !== this.#field || !this.active) return;

    // The results go; the menu stays open.
    event.preventDefault();
    event.stopPropagation();
    this.reset();
  };

  /**
   * On-screen keyboard (HDR-15): the dialog keeps its 100dvh height; while results show, the part of the layout viewport the
   * keyboard covers (the body's bottom edge against the visual viewport's bottom edge) becomes --zb-kb.
   * @param {boolean} on
   */
  #watchViewport(on) {
    const vv = window.visualViewport;
    const dialog = this.#dialog;

    if (this.#viewport && vv) {
      vv.removeEventListener('resize', this.#viewport);
      vv.removeEventListener('scroll', this.#viewport);
    }
    this.#viewport = null;
    if (!on) {
      dialog?.style.removeProperty('--zb-kb');
      return;
    }
    if (!vv || !dialog) return;

    this.#viewport = () => {
      const bottom = this.#body?.getBoundingClientRect().bottom ?? window.innerHeight;
      dialog.style.setProperty('--zb-kb', `${Math.max(0, Math.round(bottom - (vv.offsetTop + vv.height)))}px`);
    };
    vv.addEventListener('resize', this.#viewport);
    vv.addEventListener('scroll', this.#viewport);
    this.#viewport();
  }
}

if (!customElements.get('zerbay-predictive')) customElements.define('zerbay-predictive', ZerbayPredictive);
if (!customElements.get('zerbay-menu')) customElements.define('zerbay-menu', ZerbayMenu);
if (!customElements.get('zerbay-header')) customElements.define('zerbay-header', ZerbayHeader);

/* Theme editor: the header section unloads before it is replaced. Close at once; the elements re-connect on load. */
document.addEventListener('shopify:section:unload', (event) => {
  const menu = event.target instanceof Element ? event.target.querySelector('zerbay-menu') : null;
  if (menu instanceof ZerbayMenu) menu.close({ instant: true, noFocus: true });
  if (event.target instanceof Element) event.target.querySelector('zerbay-header')?.closePanel?.();
});
